import {inject, injectable} from "inversify";
import {LiveMatchHandlerInterface} from "@/modules/match/interfaces";
import LiveMatchEvaluator from "@/modules/match/services/LiveMatchEvaluator";
import {HomeAwayType, LiveIncidentType, TickType} from "@/modules/match/types";
import {Player} from "@/modules/team/entity";
import chance from "@/modules/base/services/chance";
import {Match, NeutralIncident} from "@/modules/match/entity";
import {BeforeMatchChangePossessionEvent} from "@/modules/match/events/BeforeMatchChangePossessionEvent";
import {AfterMatchChangePossessionEvent} from "@/modules/match/events/AfterMatchChangePossessionEvent";
import {NeutralIncidentTypeEnum} from "@/modules/match/enums";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";
import LaneCalculator from "@/modules/match/services/live-match-handlers/lane-based-goals-handler/LaneCalculator";

@injectable()
export default class PenaltyMatchHandler implements LiveMatchHandlerInterface {
  public possession: HomeAwayType = 'home';
  private liveMatchEvaluator: LiveMatchEvaluator;
  private match: Match;

  static readonly minimumPenalties = 5;

  constructor(
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @inject(EffectiveLineupSkillCalculator) private effectiveLineupSkillCalculator: EffectiveLineupSkillCalculator,
    @inject(LaneCalculator) private laneCalculator: LaneCalculator,
  ) {
  }

  priority() {
    return 0;
  };

  async onFinish(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType): Promise<void> {
  }

  async onIncident(liveMatchEvaluator: LiveMatchEvaluator, tick: LiveIncidentType): Promise<void> {
    if (
      !(tick.incident instanceof NeutralIncident)
      || tick.incident.neutralType !== NeutralIncidentTypeEnum.START_PENALTY
    ) {
      return;
    }

    this.liveMatchEvaluator = liveMatchEvaluator;
    this.match = tick.match;

    this.match.penalty = true;
    this.match.homePenaltyGoals = 0;
    this.match.awayPenaltyGoals = 0;

    let round = 0;
    let potentialGoals = PenaltyMatchHandler.minimumPenalties;
    const penalties: { [key in HomeAwayType]: number } = {home: 0, away: 0};
    const playerSort = (p: Player) => p.lineup === 1 ? 0 : this.getPlayerSkill(p);
    const players: { [key in HomeAwayType]: Player[] } = {
      home: collect(tick.evaluator.homePlayers).filter(p => !!p.lineup).sortBy(playerSort).toArray(),
      away: collect(tick.evaluator.awayPlayers).filter(p => !!p.lineup).sortBy(playerSort).toArray(),
    };

    const order = chance.bool() ? ['home', 'away'] : ['away', 'home'];
    let shouldBreak = false;
    let isDecided = false;

    do {
      potentialGoals = Math.max(1, PenaltyMatchHandler.minimumPenalties - round);

      let incident;

      for (let item of order) {
        if (item === 'home') {
          const homeWin = penalties.home > penalties.away + potentialGoals;
          const awayWin = penalties.home < penalties.away - potentialGoals;
          shouldBreak = homeWin || awayWin;
        } else {
          const homeWin = penalties.home > penalties.away + potentialGoals;
          const awayWin = penalties.home < penalties.away - potentialGoals;
          shouldBreak = homeWin || awayWin;
        }

        if (shouldBreak) {
          break;
        }

        const homeAway = item as HomeAwayType;
        const player = players[homeAway][round % players[homeAway].length];
        const goalKeeper = this.getGoalKeeper(tick);
        const playerSkill = player ? this.getPlayerSkill(player)! : 0;
        const gkSkill = goalKeeper ? this.getPlayerSkill(goalKeeper)! : 1;
        const likelihoodGoal = this.getLikelihood(gkSkill, playerSkill);

        this.log('penalty', {likelihoodGoal, player, goalKeeper});

        if (chance.bool({likelihood: likelihoodGoal})) {
          this.log('penalty: hit');
          incident = await this.liveMatchEvaluator.createGoalIncident(tick.match, this.team, this.teamOpp, tick.minute, player, true);
          penalties[homeAway] = penalties[homeAway] + 1;
          this.addPenaltyGoal(tick);
        } else {
          this.log('penalty: miss');

          incident = await this.liveMatchEvaluator.createMissedGoalIncident(tick.match, this.team, player, tick.minute);
          this.addChance(tick);
        }

        incident.penalty = true;
        await incident.save();

        await this.liveMatchEvaluator.triggerIncident(
          Object.assign({}, tick, {incident}),
        );

        await this.changePossession();
      }

      round++;

      isDecided = penalties.home > penalties.away + potentialGoals - 1
        || penalties.away > penalties.home + potentialGoals - 1;
    } while (!isDecided);

    await liveMatchEvaluator.triggerIncident(
      liveMatchEvaluator.createLiveIncident(
        await liveMatchEvaluator.createNeutralIncident(tick.match, NeutralIncidentTypeEnum.END_PENALTY)
      )
    );

    await liveMatchEvaluator.end();
  }

  async onTick(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType): Promise<void> {
  }

  private addGoal(tick: TickType) {
    this.possession === 'home'
      ? tick.match.homeGoals = tick.match.homeGoals! + 1
      : tick.match.awayGoals = tick.match.awayGoals! + 1;
  }

  private addPenaltyGoal(tick: TickType) {
    this.addGoal(tick);

    this.possession === 'home'
      ? tick.match.homePenaltyGoals = tick.match.homePenaltyGoals! + 1
      : tick.match.awayPenaltyGoals = tick.match.awayPenaltyGoals! + 1;
  }

  private addChance(tick: TickType) {
    this.possession === 'home'
      ? tick.match.homeChances = tick.match.homeChances! + 1
      : tick.match.awayChances = tick.match.awayChances! + 1;
  }

  private getGoalKeeper(tick: TickType) {
    return (this.possession === 'home' ? tick.awayPlayers : tick.homePlayers)
      .find(p => p.lineup === 1);
  }

  async changePossession() {
    this.log('switch possession');

    await this.liveMatchEvaluator.em.emitAsync(new BeforeMatchChangePossessionEvent(this.match, this.possession));

    this.possession = this.possessionOpp;

    await this.liveMatchEvaluator.em.emitAsync(new AfterMatchChangePossessionEvent(this.match, this.possession));
  }

  getPlayerSkill(player: Player) {
    return this.effectiveLineupSkillCalculator.calculateLinedupSkillPlayerByTeam(
      player.teamId === this.match.homeTeam.id ? this.match.homeTeam : this.match.awayTeam,
      player
    );
  }

  get possessionOpp(): HomeAwayType {
    return this.possession === 'home' ? 'away' : 'home';
  }

  get team() {
    return this.possession === 'home' ? this.match.homeTeam : this.match.awayTeam;
  }

  get teamOpp() {
    return this.possession !== 'home' ? this.match.homeTeam : this.match.awayTeam;
  }

  getLikelihood(x: number, y: number) {
    return Math.min(100, x / (x + y) * 100);
  }

  log(...args: any[]): void {
    console.log('Match penalty', ...args);
  }
}
